import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import CompanionProducts from "@/components/CompanionProducts";
import { useProducts } from "@/hooks/use-products";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { useKlaviyoSubscribe } from "@/hooks/use-klaviyo-subscribe";
// Hero image served from /public with a stable URL so the preload <link> in
// index.html (discovered by the preload scanner during HTML parse) matches
// the same request the <img> tag fires — eliminating LCP resource load delay.
const heroImage = "/hero-watch.webp";
// @ts-expect-error vite-imagetools query
import editorialImg from "@/assets/editorial-signup.webp?w=900&format=webp";
// @ts-expect-error vite-imagetools query
import editorialSrcSet from "@/assets/editorial-signup.webp?w=480;720;900;1200;1600&format=webp&as=srcset";
// @ts-expect-error vite-imagetools query
import sellTeaserImg from "@/assets/sell-teaser.webp?w=1200&format=webp";
// @ts-expect-error vite-imagetools query
import sellTeaserSrcSet from "@/assets/sell-teaser.webp?w=720;900;1200;1600;1920&format=webp&as=srcset";

const HeroSection = () => {
  const { t } = useLanguage();
  const textRef = useScrollReveal<HTMLDivElement>({ preset: "large", eager: true });
  return (
    <section className="relative flex items-end justify-center hero-height overflow-hidden">
      <img
        src={heroImage}
        alt="Audemars Piguet luxury watch"
        className="absolute inset-0 w-full h-full object-cover object-[center_25%] lg:object-[center_30%]"
        fetchPriority="high"
        loading="eager"
        decoding="async"
        width={1920}
        height={1080}
      />
      <div className="absolute inset-x-0 bottom-0 h-[35%] bg-gradient-to-t from-[rgba(11,11,12,0.68)] via-[rgba(11,11,12,0.35)] to-transparent" />

      <div className="relative w-full pb-[18px] md:pb-[28px] lg:pb-[36px] text-center flex items-end justify-center">
        <div ref={textRef} className="w-[85%] md:w-auto max-w-[600px]">
          <h1 className="heading-large text-foreground md:whitespace-nowrap" style={{ fontFamily: '"Motai Gallery", "Libre Baskerville", serif' }}>
            {t("hero.title")}
          </h1>
          <p className="body-medium text-secondary-foreground mt-[2px] md:mt-[3px] lg:mt-[3px] md:whitespace-nowrap">
            {t("hero.subtitle")}
          </p>
          <Link to="/archive" className="btn mt-[5px] md:mt-[5px] lg:mt-[7px] h-11 px-4 md:px-4 bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active transition-colors md:whitespace-nowrap inline-flex items-center justify-center">
            {t("hero.cta")}
          </Link>
        </div>
      </div>
    </section>
  );
};

const CatalogPreview = () => {
  const { formatPrice } = useCurrency();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const categories = [
    { key: "watches" as const, label: t("category.watches"), comingSoon: false },
    { key: "jewelry" as const, label: t("category.jewelry"), comingSoon: true },
  ];
  type CategoryKey = "watches" | "jewelry";
  const [active, setActive] = useState<CategoryKey>("watches");
  const [animKey, setAnimKey] = useState(0);
  const activeCategory = categories.find((c) => c.key === active)!;
  // Fetch newest first (handled by fetchProducts via sortKey/reverse), then
  // filter to >= $500 USD client-side and take top 10. We over-fetch (50) so
  // the $500 floor doesn't leave gaps when cheaper items are the most recent.
  const { data: rawProducts, isLoading } = useProducts(50);
  const TARGET_COUNT = 10;
  const getUsdAmount = (amount: string) => {
    const parsed = parseFloat(amount);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const premiumProducts = (rawProducts ?? []).filter((p) => {
    const amount = parseFloat(p.priceRange.minVariantPrice.amount);
    const currency = p.priceRange.minVariantPrice.currencyCode;
    // Floor is defined in USD. Only enforce when the product is priced in USD.
    if (currency !== "USD") return true;
    return !isNaN(amount) && amount >= 500;
  });

  const newestPremium = premiumProducts.slice(0, TARGET_COUNT);
  let products = newestPremium;

  if (newestPremium.length < TARGET_COUNT) {
    const usedIds = new Set(newestPremium.map((p) => p.id));
    const fillers = (rawProducts ?? [])
      .filter((p) => !usedIds.has(p.id))
      .sort(
        (a, b) =>
          getUsdAmount(b.priceRange.minVariantPrice.amount) -
          getUsdAmount(a.priceRange.minVariantPrice.amount)
      )
      .slice(0, TARGET_COUNT - newestPremium.length);

    products = [...newestPremium, ...fillers];
  }

  const sectionRef = useScrollReveal<HTMLDivElement>({ eager: true });

  const handleCategoryChange = (key: CategoryKey) => {
    setActive(key);
    setAnimKey((k) => k + 1);
  };

  const getStaggerDelay = (index: number) => {
    if (isMobile) {
      const row = Math.floor(index / 2);
      return row * 90;
    }
    const col = index % 5;
    const row = Math.floor(index / 5);
    return col * 90 + row * 60;
  };

  const skeleton = (
    <div className="grid grid-cols-2 gap-x-4 gap-y-6 md:gap-x-7 md:gap-y-10 lg:grid-cols-5 lg:gap-x-9 lg:gap-y-11">
      {Array(10).fill(null).map((_, i) => (
        <ProductCard key={i} />
      ))}
    </div>
  );

  return (
    <section className="container-site pt-3 md:pt-6 lg:pt-8 pb-6 md:pb-10 lg:pb-12">
      <div ref={sectionRef} className="container-content">
        <div className="flex justify-center gap-6 mb-6 md:mb-8 lg:mb-10">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => handleCategoryChange(cat.key)}
              className={`text-meta pb-1.5 transition-colors ${
                active === cat.key
                  ? "text-foreground border-b border-foreground"
                  : "text-muted-foreground hover:text-secondary-foreground"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {activeCategory.comingSoon ? (
          <div key={animKey} className="text-center py-20 md:py-28 animate-catalog-fade-in">
            <h3 className="heading-medium text-foreground mb-3">{t("category.comingSoon")}</h3>
            <p className="body-medium text-secondary-foreground">
              {t("category.comingSoonDesc")}
            </p>
          </div>
        ) : isLoading ? skeleton : (
          !products || products.length === 0 ? (
            <div className="text-center py-12">
              <p className="body-medium text-secondary-foreground">{t("category.noProducts")}</p>
            </div>
          ) : (
            <>
              <div key={animKey} className="grid grid-cols-2 gap-x-4 gap-y-6 md:gap-x-7 md:gap-y-10 lg:grid-cols-5 lg:gap-x-9 lg:gap-y-11">
                {products.map((product, i) => (
                  <div
                    key={product.id}
                    className="animate-catalog-fade-in opacity-0"
                    style={{ animationDelay: `${getStaggerDelay(i)}ms` }}
                  >
                    <ProductCard
                      isLatest={i < 3}
                      title={product.title}
                      price={formatPrice(
                        product.priceRange.minVariantPrice.amount,
                        product.priceRange.minVariantPrice.currencyCode
                      )}
                      imageUrl={product.images.edges[0]?.node.url}
                      imageAlt={product.images.edges[0]?.node.altText || product.title}
                      handle={product.handle}
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-center pt-8 md:pt-10 lg:pt-12">
                <Link to="/archive" className="btn h-11 px-4 border border-accent text-foreground hover:bg-accent/10 active:bg-accent/15 transition-colors inline-flex items-center justify-center">
                  {t("category.discoverAll")} {activeCategory.label}
                </Link>
              </div>
            </>
          )
        )}
      </div>
    </section>
  );
};

const SellTeaser = () => {
  const { t } = useLanguage();
  const contentRef = useScrollReveal<HTMLDivElement>({ preset: "large" });
  return (
    <section className="py-14 md:py-20 lg:py-24">
      <div className="container-site">
        <div className="container-content">
          <div className="relative overflow-hidden sell-teaser-height">
            <img
              src={sellTeaserImg}
              srcSet={sellTeaserSrcSet}
              sizes="(max-width: 989px) 100vw, min(100vw, 1280px)"
              alt="Luxury watch and jewelry on leather"
              loading="lazy"
              decoding="async"
              width={1600}
              height={900}
              className="absolute inset-0 w-full h-full object-cover object-center lg:object-[center_top]"
            />
            <div className="absolute inset-0 bg-background/35 lg:bg-background/45" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background/70 to-transparent" />

            <div ref={contentRef} className="relative h-full flex flex-col justify-end lg:justify-center items-center text-center px-5 md:px-10 pb-10 md:pb-0 [text-shadow:0_2px_16px_rgba(0,0,0,0.85),0_0_40px_rgba(0,0,0,0.6),0_4px_30px_rgba(0,0,0,0.5)]">
              <h2 className="heading-large text-foreground mb-3 md:mb-4">
                {t("sell.title")}
              </h2>
              <p className="body-medium max-w-md mb-5 md:mb-7 text-primary">
                {t("sell.subtitle")}
              </p>
              <button className="btn h-11 px-4 md:px-4 bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active transition-colors [text-shadow:none]">
                {t("sell.cta")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const EditorialSignupSection = () => {
  const { t } = useLanguage();
  const imageRef = useScrollReveal<HTMLDivElement>();
  const contentRef = useScrollReveal<HTMLDivElement>({ preset: "default", delay: 100 });
  const formRef = useScrollReveal<HTMLFormElement>({ preset: "small", delay: 140 });
  const { email, setEmail, submit, loading, message, isSuccess, isError } =
    useKlaviyoSubscribe({
      source: "homepage_private_list",
      signupLocation: "homepage",
      formName: "Join the Private List",
    });
  return (
    <section className="container-site py-14 md:py-20 lg:py-24">
      <div className="container-content">
        <div className="bg-card flex flex-col lg:flex-row overflow-hidden">
          <div ref={imageRef} className="relative w-full lg:w-1/2">
            <div className="aspect-[4/3] lg:aspect-auto lg:absolute lg:inset-0">
              <img
                src={editorialImg}
                srcSet={editorialSrcSet}
                sizes="(max-width: 1023px) 100vw, min(50vw, 640px)"
                alt="Luxury watches and jewelry collection"
                loading="lazy"
                decoding="async"
                width={900}
                height={675}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="w-full lg:w-1/2 flex flex-col items-center justify-center text-center px-5 py-8 md:px-10 md:py-12 lg:px-14 lg:py-16">
            <div ref={contentRef}>
              <h2 className="heading-large text-foreground mb-3 md:mb-4">
                {t("newsletter.title")}
              </h2>
              <p className="body-medium text-secondary-foreground max-w-xs mb-5 md:mb-6">
                {t("newsletter.subtitle")}
              </p>
            </div>

            <form ref={formRef} onSubmit={submit} noValidate className="flex w-full max-w-sm flex-col items-stretch gap-2">
              <div className="flex items-stretch gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  placeholder={t("newsletter.placeholder")}
                  className="m-0 flex-1 h-11 bg-input border border-border px-4 body-small text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring transition-colors disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="m-0 h-11 px-4 md:px-4 bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "…" : t("newsletter.subscribe")}
                </button>
              </div>
              {message && (
                <p
                  role={isError ? "alert" : "status"}
                  className={`text-meta ${isSuccess ? "text-foreground" : "text-muted-foreground"}`}
                >
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const SectionDivider = () => (
  <div className="container-site">
    <div className="container-content">
      <div className="border-t border-border" />
    </div>
  </div>
);

const Index = () => {
  return (
    <SiteLayout>
      <Seo
        title="MOTAI — Rare Pre-Owned Watches & Fine Jewelry"
        description="MOTAI is a private dealer of rare, pre-owned luxury watches and fine jewelry. Each piece is authenticated, unique, and offered with discretion."
        path="/"
      />
      <HeroSection />
      <CatalogPreview />
      <SectionDivider />
      <CompanionProducts />
      <SectionDivider />
      <SellTeaser />
      <SectionDivider />
      <EditorialSignupSection />
    </SiteLayout>
  );
};

export default Index;
